import {initialGameConfig, initialLanguageConfig} from '../../constants';
import {IAppliedRule} from '../../incase-domain/appliedRules';
import {DeviceType} from '../../incase-domain/common/DeviceType.enum';
import {IPresentation} from '../../incase-domain/presentation';
import {ProtectionInfo} from '../../incase-domain/presentationProtection';
import {IPresentationShare} from '../../incase-domain/presentationShare';
import {ISlidePreview} from '../../incase-domain/proptypes/slidePreview';
import {ISlide} from '../../incase-domain/slide';
import {swapArrayIndices} from '../../utils/utils';
import {SWAP_SELECTED_SLIDES} from '../actions/edit';
import {
    EXPORT_PRESENTATION,
    EXPORT_PRESENTATION_END,
    RESET_PRESENTATION_REDUCER,
    SET_DELETED_EMITTED_SHARED_PRESENTATION,
    SET_EMITTED_SHARED_PRESENTATION,
    SET_PRESENTATION_SETTINGS_MODAL,
    SET_PRESENTATION_VALIDATION_ERRORS,
    SET_SELECTED_VIEW,
    UPDATE_SELECTED_PRESENTATION,
    UPDATE_SELECTED_PRESENTATION_APPLIED_RULES,
    UPDATE_SELECTED_PRESENTATION_SLIDE_INDEX_LIST,
    UPDATE_SELECTED_PRESENTATION_SLIDES,
    UPDATE_SETTINGS_CONFIG,
} from '../actions/presentation';
import {HIDE_LOADING_SPINNER, SHOW_LOADING_SPINNER, UPDATE_SELECTED_SLIDE_PREVIEWS} from '../actions/slide.preview';
import {EditReducerType} from '../actions/types/edit.types';
import {PresentationReducerType} from '../actions/types/presentation.types';
import {SlidePreviewReducerType} from '../actions/types/slide.preview.types';

export interface PresentationReducerState {
    selectedPresentation: IPresentation;
    selectedPresentationSlidePreviews: ISlidePreview[];
    previewsLoader: boolean;
    presentationProtection: ProtectionInfo;
    exportingPresentations: string[];
    presentationValidationErrors: Record<string, string[]>;
    emittedSharedPresentation: IPresentationShare;
    deletedEmittedSharedPresentationIds: string[];
    selectedView: DeviceType;
    presentationSettingsModal: {open: boolean; tab?: number};
}

const preloadedPresentationState = {
    selectedPresentation: {
        _id: '',
        slides: [] as ISlide[],
        name: '',
        favorite: false,
        presentationBackground: '',
        presentationBackgroundName: '',
        presentationMobileBackground: '',
        presentationMobileBackgroundName: '',
        gameConfig: {...initialGameConfig},
        languageConfig: {...initialLanguageConfig},
        scrollButtonsOn: true,
        slideIndexList: [] as string[],
        created: '',
        lastModified: '',
        appliedRules: [] as IAppliedRule[],
    } as IPresentation,
    selectedPresentationSlidePreviews: [] as ISlidePreview[],
    presentationProtection: {types: [], _id: ''},
    presentationSettingsModal: {open: false, tab: 0},
    selectedView: DeviceType.DESKTOP,
};

export const preloadedState = {
    previewsLoader: false,
    exportingPresentations: [] as string[],
    presentationValidationErrors: {} as Record<string, string[]>,
    emittedSharedPresentation: {} as IPresentationShare,
    deletedEmittedSharedPresentationIds: [],
    ...preloadedPresentationState,
};

export const presentationReducer = (
    state: PresentationReducerState = preloadedState,
    action: PresentationReducerType | EditReducerType | SlidePreviewReducerType
): PresentationReducerState => {
    switch (action.type) {
        case UPDATE_SELECTED_PRESENTATION:
            return {...state, selectedPresentation: action.presentation};
        case UPDATE_SELECTED_PRESENTATION_SLIDES:
            return {...state, selectedPresentation: {...state.selectedPresentation, slides: action.slides}};
        case UPDATE_SELECTED_PRESENTATION_SLIDE_INDEX_LIST:
            return {...state, selectedPresentation: {...state.selectedPresentation, slideIndexList: action.slideIndexList}};
        case SWAP_SELECTED_SLIDES: {
            const {indices} = action;

            return {
                ...state,
                selectedPresentation: {
                    ...state.selectedPresentation,
                    slides: swapArrayIndices(state.selectedPresentation.slides, indices.newIndex, indices.oldIndex),
                },
            };
        }
        case UPDATE_SELECTED_PRESENTATION_APPLIED_RULES:
            return {
                ...state,
                selectedPresentation: {
                    ...state.selectedPresentation,
                    appliedRules: action.appliedRules,
                },
            };
        case UPDATE_SELECTED_SLIDE_PREVIEWS:
            return {
                ...state,
                selectedPresentationSlidePreviews: action.payload,
            };
        case SHOW_LOADING_SPINNER:
            return {
                ...state,
                previewsLoader: true,
            };
        case HIDE_LOADING_SPINNER:
            return {
                ...state,
                previewsLoader: false,
            };
        case UPDATE_SETTINGS_CONFIG:
            return {
                ...state,
                presentationProtection: action.protection,
            };
        case EXPORT_PRESENTATION:
            return {
                ...state,
                exportingPresentations: [...state.exportingPresentations, action.presentationId],
            };
        case EXPORT_PRESENTATION_END:
            return {
                ...state,
                exportingPresentations: state.exportingPresentations.filter((e) => e !== action.presentationId),
            };
        case SET_PRESENTATION_VALIDATION_ERRORS: {
            return {...state, presentationValidationErrors: action.errors};
        }
        case SET_EMITTED_SHARED_PRESENTATION: {
            return {...state, emittedSharedPresentation: action.sharedPresentation};
        }
        case SET_DELETED_EMITTED_SHARED_PRESENTATION: {
            return {...state, deletedEmittedSharedPresentationIds: action.presentationIds};
        }
        case RESET_PRESENTATION_REDUCER:
            return {...state, ...preloadedPresentationState};
        case SET_SELECTED_VIEW:
            return {...state, selectedView: action.selectedView};
        case SET_PRESENTATION_SETTINGS_MODAL:
            return {...state, presentationSettingsModal: action.payload};
        default:
            return state;
    }
};
